import React, { FC, ReactElement, useRef, useState } from 'react'
import { Upload, Typography } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import axios from 'axios'
import { SampleDocUploaderProps } from './types'

const { Dragger } = Upload

const CONCURRENCY = 4

interface QueueItem {
  file: File
  onSuccess: (body: string) => void
  onError: (error: Error) => void
  onProgress: (event: { percent: number }) => void
}

const SampleDocUploader: FC<SampleDocUploaderProps> = ({
  uploadPath,
  csrfToken,
  uploadedDocs,
  targetDocs
}): ReactElement => {
  const thresholdMet = targetDocs !== null && uploadedDocs >= (targetDocs ?? Infinity)
  const [fileList, setFileList] = useState<any[]>([])
  const queue = useRef<QueueItem[]>([])
  const activeCount = useRef(0)
  const results = useRef<boolean[]>([])

  const uploadFile = (item: QueueItem): void => {
    activeCount.current++
    const formData = new FormData()
    formData.append('file', item.file)

    axios
      .post(uploadPath, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-CSRF-Token': csrfToken },
        onUploadProgress: (e) => {
          if (e.total) item.onProgress({ percent: Math.round((e.loaded * 100) / e.total) })
        }
      })
      .then(() => {
        item.onSuccess('ok')
        results.current.push(true)
      })
      .catch((e) => {
        item.onError(e)
        results.current.push(false)
      })
      .finally(() => {
        activeCount.current--
        if (queue.current.length > 0) {
          uploadFile(queue.current.shift()!)
        } else if (activeCount.current === 0) {
          const allSucceeded = results.current.every(Boolean)
          results.current = []
          if (allSucceeded) window.location.reload()
        }
      })
  }

  const draggerProps = {
    accept: 'application/pdf',
    multiple: true,
    customRequest: ({ file, onSuccess, onError, onProgress }: any) => {
      const item: QueueItem = { file, onSuccess, onError, onProgress }
      if (activeCount.current < CONCURRENCY) {
        uploadFile(item)
      } else {
        queue.current.push(item)
      }
    },
    onChange: ({ fileList: newList }: any) => setFileList(newList),
    fileList
  }

  return (
    <div>
      <Typography.Title
        level={4}
        style={{ margin: '0 0 12px', fontSize: 16, paddingBottom: 12, borderBottom: '1px solid #f3f4f5' }}
      >
        Upload Documents
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Upload PDF documents for sample processing. Files are uploaded to your company&apos;s secure document storage.
        {thresholdMet &&
          ' The requested number of documents has been met — additional uploads are welcome if you have more relevant samples to contribute.'}
      </Typography.Paragraph>

      <Dragger {...draggerProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag PDF files here to upload</p>
        <p className="ant-upload-hint">Supports multiple files. PDFs only.</p>
      </Dragger>
    </div>
  )
}

export default SampleDocUploader
